import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Plus, Pencil, Trash2, Users, X, Save, Shield } from 'lucide-react';

const UserManager = () => {
  const { api } = useAuth();
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ username: '', email: '', password: '', role: 'viewer', active: true });

  const load = () => api.get('/admin/users').then(r => setUsers(r.data || [])).catch(console.error);
  useEffect(() => { load(); }, []);

  const resetForm = () => { setForm({ username: '', email: '', password: '', role: 'viewer', active: true }); setEditing(null); setShowForm(false); };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        const payload = { ...form };
        if (!payload.password) delete payload.password;
        await api.put(`/admin/users/${editing}`, payload);
      } else {
        await api.post('/admin/users', form);
      }
      resetForm(); load();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  const handleEdit = (u) => {
    setForm({ username: u.username, email: u.email || '', password: '', role: u.role || 'viewer', active: u.active !== false });
    setEditing(u.id); setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this user?')) return;
    await api.delete(`/admin/users/${id}`); load();
  };

  const roles = [
    { value: 'admin', label: 'Admin', desc: 'Full access', color: '#f59e0b' },
    { value: 'moderator', label: 'Moderator', desc: 'Videos, comments, stories', color: '#3b82f6' },
    { value: 'editor', label: 'Editor', desc: 'Upload videos & stories only', color: '#10b981' },
    { value: 'viewer', label: 'Viewer', desc: 'View only', color: '#888' },
  ];

  const getRoleColor = (role) => {
    const r = roles.find(rr => rr.value === role);
    return r ? r.color : '#888';
  };

  return (
    <div className="admin-section">
      <div className="section-header">
        <h2 className="section-title"><Users size={20} /> User Management ({users.length})</h2>
        <button className="btn-primary" onClick={() => { resetForm(); setShowForm(true); }}><Plus size={16} /> Add User</button>
      </div>

      {showForm && (
        <div className="admin-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 className="card-subtitle" style={{ margin: 0 }}>{editing ? 'Edit User' : 'Create New User'}</h3>
            <button className="btn-sm btn-delete" onClick={resetForm}><X size={14} /></button>
          </div>
          <form onSubmit={handleSave}>
            <div className="two-col">
              <div>
                <label className="form-label">Username *</label>
                <input className="form-input" value={form.username} onChange={e => setForm({...form, username: e.target.value})} required placeholder="username" />
              </div>
              <div>
                <label className="form-label">Email</label>
                <input className="form-input" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="email@example.com" />
              </div>
              <div>
                <label className="form-label">{editing ? 'New Password (kosongkan jika tidak diubah)' : 'Password *'}</label>
                <input className="form-input" type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})}
                  required={!editing} minLength={6} placeholder="Min 6 characters" />
              </div>
              <div>
                <label className="form-label">Role *</label>
                <select className="form-select" value={form.role} onChange={e => setForm({...form, role: e.target.value})}>
                  {roles.map(r => <option key={r.value} value={r.value}>{r.label} — {r.desc}</option>)}
                </select>
              </div>
            </div>
            <div className="toggle-wrap" style={{ marginTop: 12 }}>
              <button type="button" className={`toggle-switch ${form.active ? 'active' : ''}`} onClick={() => setForm({...form, active: !form.active})} />
              <span className="toggle-label">Active Account</span>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button type="submit" className="btn-primary"><Save size={16} /> {editing ? 'Update User' : 'Create User'}</button>
              <button type="button" className="btn-secondary" onClick={resetForm}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      <div className="admin-card" style={{ padding: 0, overflow: 'hidden' }}>
        <table className="admin-table">
          <thead>
            <tr><th>User</th><th>Email</th><th>Role</th><th>Status</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {users.length === 0 ? (
              <tr><td colSpan={5} style={{ textAlign: 'center', padding: 30, color: '#555' }}>No users yet. You are the main admin.</td></tr>
            ) : users.map(u => (
              <tr key={u.id}>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div className="user-avatar" style={{ width: 32, height: 32, fontSize: 12 }}>{(u.username || 'U')[0].toUpperCase()}</div>
                    <span style={{ fontWeight: 700, color: '#ddd' }}>{u.username}</span>
                  </div>
                </td>
                <td style={{ color: '#888', fontSize: 12 }}>{u.email || '-'}</td>
                <td><span style={{ fontSize: 11, fontWeight: 700, color: getRoleColor(u.role), textTransform: 'uppercase' }}><Shield size={10} /> {u.role}</span></td>
                <td><span className={`badge ${u.active !== false ? 'badge-success' : 'badge-danger'}`}>{u.active !== false ? 'Active' : 'Inactive'}</span></td>
                <td>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button className="btn-sm btn-edit" onClick={() => handleEdit(u)}><Pencil size={12} /></button>
                    <button className="btn-sm btn-delete" onClick={() => handleDelete(u.id)}><Trash2 size={12} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UserManager;
